import { FC, forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { Box, Popper, ClickAwayListener, Typography, SxProps, Theme } from '@mui/material';
import { DateField, TimeField, StaticDatePicker } from '@mui/x-date-pickers';
import { addHours, eachMinuteOfInterval, endOfDay, format, isAfter, isBefore, isValid, max, min, startOfDay } from 'date-fns';
import { applyTzForDate } from '../hooks';
import { CoreFormControl, CoreFormControlProps } from '../../_core';
import { eventDateTimePickerStyles } from './event-date-time-picker.styles';
import {
	Colors,
	inputBackgroundColor,
	inputBorderColor,
	inputDisabledBorderColor,
	inputDisabledForegroundColor,
	inputErrorBorderColor,
	inputForegroundColor,
} from '../../../constants';
import { CalendarIcon as Calendar, CheckIcon } from '../../../icons';
import Showable from '../../utils/showable';
import { clsx } from '../../../utils';

export interface EventDateTimePickerProps extends CoreFormControlProps {
	value?: Date | string | number | null;
	dateFormat?: string;
	timeFormat?: string;
	disablePast?: boolean;
	CalendarIcon?: FC;
	minDate?: Date;
	maxDate?: Date;
	timezone?: string;
	dateFieldSx?: SxProps<Theme>;
	timeFieldSx?: SxProps<Theme>;
	onChange?(value: Date | null): void;
}

export const EventDateTimePicker: FC<EventDateTimePickerProps> = forwardRef((props, ref) => {
	const {
		id,
		disabled,
		error,
		helperText,
		label,
		requiredMark,
		focused,
		fullWidth,
		controlSx,
		labelSx,
		dateFieldSx,
		timeFieldSx,
		value,
		disablePast = false,
		timezone = 'local',
		dateFormat = 'MMM dd',
		timeFormat = 'h:mm aaa',
		CalendarIcon = Calendar,
		onChange,
	} = props;

	const [isDatePopperOpen, setIsDatePopperOpen] = useState(false);
	const [isTimePopperOpen, setIsTimePopperOpen] = useState(false);
	const dateFieldAnchorRef = useRef(null);
	const timeFieldAnchorRef = useRef(null);

	const now = useMemo(() => applyTzForDate(new Date(), 'UTC', timezone), [timezone]); // Current date-time in the time zone
	const date = useMemo(() => (value ? applyTzForDate(new Date(value), 'UTC', timezone) : undefined), [timezone, value]);
	const minDate = useMemo(
		() => (props.minDate ? applyTzForDate(new Date(props.minDate), 'UTC', timezone) : undefined),
		[timezone, props.minDate]
	);
	const maxDate = useMemo(
		() => (props.maxDate ? applyTzForDate(new Date(props.maxDate), 'UTC', timezone) : undefined),
		[timezone, props.maxDate]
	);

	const closeDatePopper = useCallback((): void => {
		setIsDatePopperOpen(false);
	}, []);

	const openDatePopper = useCallback(() => {
		if (disabled) {
			return;
		}

		setIsDatePopperOpen(true);
	}, [disabled]);

	const timeSlots = useMemo(() => [
		// start from 8:00 AM
		...eachMinuteOfInterval({
			start: addHours(startOfDay(date ?? now), 8),
			end: endOfDay(date ?? now)
		}, { step: 15 }),
		...eachMinuteOfInterval({
			start: startOfDay(date ?? now),
			end: addHours(startOfDay(date ?? now), 8),
		}, { step: 15 }),
	], [date, now]);

	const closeTimePopper = useCallback((): void => {
		setIsTimePopperOpen(false);
	}, []);

	const openTimePopper = useCallback(() => {
		if (disabled) {
			return;
		}

		setIsTimePopperOpen(true);
	}, [disabled]);

	const handleChange = useCallback((date: Date | null): void => {
		let newDate = date;

		if (minDate && newDate) {
			newDate = max([newDate, minDate])
		}

		if (maxDate && newDate) {
			newDate = min([newDate, maxDate])
		}

		onChange?.(newDate ? applyTzForDate(newDate, 'local', timezone) : null);
	}, [maxDate, minDate, onChange, timezone]);

	return (
		<CoreFormControl
			error={error}
			helperText={helperText}
			label={label}
			id={id}
			requiredMark={requiredMark}
			focused={focused}
			fullWidth={fullWidth}
			controlSx={controlSx}
			labelSx={labelSx}
		>
			<Box
				sx={{
					width: '100%',
					height: '40px',
					borderRadius: '8px',
					p: '10px 16px',
					boxSizing: 'border-box',
					borderWidth: '1px',
					borderStyle: 'solid',
					display: 'flex',
					alignItems: 'center',
					backgroundColor: inputBackgroundColor,
					color: disabled ? inputDisabledForegroundColor : inputForegroundColor,
					borderColor: disabled ? inputDisabledBorderColor : error ? inputErrorBorderColor : inputBorderColor,

					'& > svg': {
						height: '18px',
						width: '18px',
						mr: '10px',
					},
				}}
			>
				<Box
					component={CalendarIcon}
					sx={{
						width: '20px',
						height: '20px',
					}}
				/>
				<Box flex={1} display="flex" alignItems="center" gap="4px">
					<ClickAwayListener onClickAway={closeDatePopper}>
						<Box>
							<DateField
								value={date}
								disabled={disabled}
								format={dateFormat}
								onClick={openDatePopper}
								ref={dateFieldAnchorRef}
								onChange={value => handleChange?.(value && isValid(value) ? value : null)}
								sx={clsx({
									'& .MuiInputBase-root': {
										p: 0,
										borderRadius: '4px',
										backgroundColor: Colors.black_a10,
										fontFamily: 'Montserrat, sans-serif',
										fontSize: '14px',
										fontWeight: 600,
										width: '70px',

										'& .MuiInputBase-input': {
											p: '2px 4px',
											textAlign: 'center',
										},

										'& .MuiOutlinedInput-notchedOutline': {
											display: 'none',
										},
									},
								}, dateFieldSx)}
							/>
							<Popper
								open={isDatePopperOpen}
								anchorEl={dateFieldAnchorRef.current}
								placement="bottom-start"
								disablePortal={true}
								sx={{
									zIndex: 1001,
								}}
							>
								<StaticDatePicker
									value={date}
									disabled={disabled}
									shouldDisableDate={date => (
										(disablePast && isBefore(date, startOfDay(now)))
										|| (!!minDate && isBefore(date, startOfDay(minDate)))
										|| (!!maxDate && isAfter(date, endOfDay(maxDate)))
									)}
									slots={{
										toolbar: () => null,
										actionBar: () => null,
									}}
									sx={eventDateTimePickerStyles}
									onChange={value => handleChange?.(value)}
								/>
							</Popper>
						</Box>
					</ClickAwayListener>
					<ClickAwayListener onClickAway={closeTimePopper}>
						<Box>
							<TimeField
								value={date}
								disabled={disabled}
								format={timeFormat}
								ref={timeFieldAnchorRef}
								onClick={openTimePopper}
								onChange={value => handleChange?.(value && isValid(value) ? value : null)}
								sx={clsx({
									'& .MuiInputBase-root': {
										p: 0,
										borderRadius: '4px',
										backgroundColor: Colors.black_a10,
										fontFamily: 'Montserrat, sans-serif',
										fontSize: '14px',
										fontWeight: 600,
										width: '80px',

										'& .MuiInputBase-input': {
											p: '2px 4px',
											textAlign: 'center',
										},

										'& .MuiOutlinedInput-notchedOutline': {
											display: 'none',
										},
									},
								}, timeFieldSx)}
							/>
							<Popper
								open={isTimePopperOpen}
								anchorEl={timeFieldAnchorRef.current}
								placement="bottom-start"
								disablePortal={true}
								sx={{
									zIndex: 1001,
								}}
							>
								<Box
									sx={{
										borderRadius: '4px',
										backgroundColor: Colors.black_a60,
										width: '115px',
										height: '318px',
										overflow: 'auto',
										backdropFilter: 'blur(20px)',

										'&::-webkit-scrollbar': {
											width: '4px',
										},

										'&::-webkit-scrollbar-track': {
											borderRadius: '4px',
											backgroundColor: Colors.white_a10,
										},

										'&::-webkit-scrollbar-thumb': {
											borderRadius: '4px',
											backgroundColor: Colors.white_a30,
										},
									}}
								>
									{timeSlots.map(time => {
										const isSelected = !!date
											&& date.getHours() === time.getHours()
											&& date.getMinutes() === time.getMinutes();
										const disabled = (disablePast && isBefore(time, now))
											|| (!!minDate && isBefore(time, minDate))
											|| (!!maxDate && isAfter(time, maxDate));

										return (
											<Box
												key={time.toISOString()}
												sx={clsx(
													{
														userSelect: 'none',
														p: '10px 13px',
														display: 'flex',
														justifyContent: 'space-between',
														alignItems: 'center',
														color: disabled ? Colors['969696'] : Colors.white,
													},
													!disabled ? {
														cursor: 'pointer',

														'&:hover': {
															backgroundColor: Colors.white_a5,
														},
													} : undefined,
												)}
												onClick={() => {
													if (disabled) {
														return;
													}

													const newValue = date ? new Date(date) : now;
													newValue.setHours(time.getHours());
													newValue.setMinutes(time.getMinutes());

													handleChange?.(newValue);
												}}
											>
												<Typography
													sx={{
														flex: 1,
														fontFamily: 'Montserrat, sans-serif',
														fontSize: '13px',
														fontWeight: 600,
													}}
												>
													{format(time, 'h:mm aa')}
												</Typography>
												<Showable show={isSelected}>
													<CheckIcon />
												</Showable>
											</Box>
										);
									})}
								</Box>
							</Popper>
						</Box>
					</ClickAwayListener>
				</Box>
			</Box>
		</CoreFormControl>
	);
});

EventDateTimePicker.displayName = 'EventDateTimePicker';
